/**
 * resolveOrigin — derive the origin for config fetching.
 *
 * Precedence (first match wins):
 *   1. overrides.apiUrl set → use directly
 *   2. overrides.baseHostname set → derive https://api.{hostname}
 *   3. localhost/127.0.0.1 → LOCAL_API_ORIGIN (this checkout's API port)
 *   4. Everything else → window.location.origin
 *
 * On deaddrop domains, configuration.json is served from CloudFront
 * (same origin as the SPA). The actual API URL comes from the config
 * response, not from URL derivation.
 */

import type { ResolveConfigOptions } from './types';

/**
 * Local API origin for this checkout. vite.config.ts injects it from API_PORT
 * (scripts/ports.sh, formula in ~/projects/dev-ports/PORTS.md) so each worktree's bundle
 * targets its own API; the fallback is the main checkout's port.
 */
export const LOCAL_API_ORIGIN: string =
  import.meta.env.VITE_LOCAL_API_ORIGIN ?? 'http://localhost:10010';

/**
 * Check if the hostname belongs to a production deaddrop domain.
 *
 * Matches ddrp.sh, *.ddrp.sh, deaddrop.sh, and *.deaddrop.sh.
 * Does NOT match localhost, 127.0.0.1, or deaddrop.bezoan.com
 * (the dev nginx reverse proxy).
 */
export function isRealDomain(hostname: string): boolean {
  return (
    hostname.endsWith('.ddrp.sh') ||
    hostname === 'ddrp.sh' ||
    hostname.endsWith('.deaddrop.sh') ||
    hostname === 'deaddrop.sh'
  );
}

/**
 * Derive the origin URL for config fetching.
 *
 * Returns an origin string with no trailing slash.
 */
export function resolveOrigin(options: ResolveConfigOptions): string {
  const { overrides = {} } = options;

  // 1. Explicit apiUrl override (falsy values treated as unset).
  if (overrides.apiUrl) {
    return overrides.apiUrl.replace(/\/$/, '');
  }

  // 2. Base hostname → derive API URL.
  if (overrides.baseHostname) {
    return `https://api.${overrides.baseHostname}`;
  }

  // 3. Localhost → local API server.
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return LOCAL_API_ORIGIN;
  }

  // 4. Default: same origin (CloudFront on deaddrop domains, proxy elsewhere).
  return window.location.origin;
}
